import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';

import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';

import Button from '../../Button';
import { doDeleteStudent, doGetClassStudents } from '../../../store/classStudent.slice';
import { errorVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';
import { getCommonCodeLabel } from '../../../lib/store';
import QueryString from 'qs';
import { IStudent } from '../../../models/campusClasses2.model';
import ShowScoreModal from '../../modals/ShowScoreModal';
import useNotification from '../../../hooks/useNotification';
import { getRefundedOrdersTodo } from '../../../api/refunds.api';

interface IProps {
  setTab: (tab: string) => void;
  memberCode: string;
  setMemberCode: (memberCode: string) => void;
  classStudentCode: string;
  setClassStudentCode: (classStudentCode: string) => void;
}

const ClassStudentList: FC<IProps> = ({
                                        setTab,
                                        memberCode,
                                        setMemberCode,
                                        classStudentCode,
                                        setClassStudentCode,
                                      }) => {
  const { writeAuthYn } = useAuth();
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { warningNotification, infoNotification } = useNotification();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { chosenClass } = useSelector((state: RootState) => state.class);
  const { students } = useSelector((state: RootState) => state.classStudent);
  const { currentSemester } = useSelector((state: RootState) => state.semester);

  const [showScoreVisible, setShowScoreVisible] = useState<any>(false);


  // 학생 출결 표시
  const onViewAttendance = (record: any) => {
    sessionStorage.setItem('memberKorName', record.memberKorName);
    sessionStorage.setItem('memberEngName', record.memberEngName);
    sessionStorage.setItem('classStudentCode', record.classStudentCode);
    setTab('attendance');
  };

  // 학생 삭제
  const deleteStudent = async (classStudentCode: number) => {
    const classCode = chosenClass?.classCode;
    const semesterCode = chosenClass?.semesterCode as number;
    if (classCode && semesterCode) {
      const res = await dispatch(doDeleteStudent(classStudentCode));

      switch (res?.status) {
        case 200:
          dispatch(doGetClassStudents(classCode, semesterCode));
          break;
        case 400:
          warningNotification(res.data.data);
          break;
        case 401:
          dispatch(doRemoveAuth());
          break;
        case 403:
          enqueueSnackbar(t('common.message.forbidden'), warningVariant);
          history.push('/');
          break;
        default:
          dispatch(doGetClassStudents(classCode, semesterCode));
          enqueueSnackbar(t('common.message.error'), errorVariant);
      }
    }
  };

  // 학생 삭제/퇴원
  const onDeleteStudent = async (record: any) => {
    if (record.classStudentStatusCode === 'PL') {
      deleteStudent(record.classStudentCode);
    } else {
      const { data: res } = await getRefundedOrdersTodo(record.memberCode);

      console.log(res);
      if (res.data && res.data.length > 0 && res.data.filter(it => it.refundSttCode === 'WA').length > 0) {
        infoNotification(t('환불 대기 건이 존재합니다. 환불을 완료 후 다시 시도해 주세요'));
        return;
      }

      sessionStorage.setItem('memberKorName', record.memberKorName);
      sessionStorage.setItem('memberEngName', record.memberEngName);
      sessionStorage.setItem('classStudentCode', record.classStudentCode);
      setTab('retire');
    }
  };


  const columns: TableColumnsType<IStudent> = [
    {
      title: 'No.',
      dataIndex: 'classStudentCode',
      align: 'center',
      sorter: (a, b) => a.classStudentCode - b.classStudentCode,
      render: (value, record, index) => index + 1,
    },
    {
      title: t('class.class.column29'),
      dataIndex: 'memberKorName',
      align: 'center',
      sorter: (a, b) => a.memberKorName?.localeCompare(b.memberKorName),
    },
    {
      title: t('class.class.column30'),
      dataIndex: 'schoolName',
      align: 'center',
      sorter: (a, b) => a.schoolName?.localeCompare(b.schoolName),
    },
    {
      title: t('class.class.column31'),
      dataIndex: 'gradeCode',
      align: 'center',
      sorter: (a, b) => a.gradeCode?.localeCompare(b.gradeCode),
      render: (value) => getCommonCodeLabel('SY0006', value, 'ko'),
    },
    {
      title: t('class.class.column32'),
      dataIndex: 'genderDiv',
      align: 'center',
      sorter: (a, b) => a.genderDiv?.localeCompare(b.genderDiv),
      render: (value) =>
        getCommonCodeLabel('MB0006', value?.trim() || '', 'ko'),
    },
    {
      title: t('class.class.column33'),
      dataIndex: 'studySrtDate',
      align: 'center',
      sorter: (a, b) => a.studySrtDate?.localeCompare(b.studySrtDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD'),
    },
    {
      title: t('class.class.column34'),
      dataIndex: '',
      align: 'center',
      render: (value, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClickCapture={(e) => {
              e.stopPropagation();
              onViewAttendance(record);
            }}
            text={t('class.class.column34')}
          />
        </div>
      ),
    },
    {
      title: t('class.class.column35'),
      dataIndex: 'memberCode',
      align: 'center',
      render: (value) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClickCapture={(e) => {
              e.stopPropagation();
              setMemberCode(value);
              setShowScoreVisible(true);
            }}
            text={t('class.class.column35')}
          />
        </div>
      ),
    },
    {
      title: t('class.class.column36'),
      dataIndex: 'classStudentCode',
      align: 'center',
      render: (value: number, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small mint"
            onClickCapture={(e) => {
              e.stopPropagation();
              onDeleteStudent(record);
            }}
            text={
              record.classStudentStatusCode === 'PL'
                ? t('common.button.delete')
                : t('class.class.column36')
            }
            writeAuthYn={writeAuthYn}
            disabled={
              !(
                record.classStudentStatusName === '등록' ||
                record.classStudentStatusName === '예약'
              )
            }
          />
        </div>
      ),
    },
    {
      title: t('class.class.column38'),
      dataIndex: 'classStudentCode',
      align: 'center',
      render: (value: string, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small pink-line"
            onClickCapture={(e) => {
              e.stopPropagation();
              setMemberCode(record.memberCode);
              setClassStudentCode(value);
              setTab('transfer');
            }}
            text={t('class.class.column38')}
            writeAuthYn={writeAuthYn}
            disabled={record.classStudentStatusName !== '등록' || chosenClass?.semesterType === 'SC'}
          />
        </div>
      ),
    },
    {
      title: t('class.class.column39'),
      dataIndex: 'classStudentStatusCode',
      align: 'center',
      render: (value) =>
        getCommonCodeLabel('CL0003', value?.trim() || '', i18n.language),
      // CL0003
      sorter: (a, b) =>
        a.classStudentStatusName?.localeCompare(b.classStudentStatusName),
    },
    {
      title: t('class.class.column110'),
      dataIndex: 'orderStt',
      align: 'center',
      render: (value) => (value === 'Y' ? 'O' : 'X'),
    },
    {
      title: t('class.course.count'),
      dataIndex: 'studentRegisterCount',
      align: 'center',
      render: (value) => value ?? 0,
    },
  ];

  return (
    <>
      <div className="comm-bx__between">
        <h3 className="sub-title">
          {chosenClass?.className} {`(${t('class.class.text12')})`}
        </h3>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={() => setTab('allocateStudent')}
            text={t('class.class.button4')}
            writeAuthYn={writeAuthYn}
            disabled={chosenClass?.classStatus === 'CL'}
          />
        </div>
      </div>
      <div className="comm-table__area">
        <Table
          columns={columns}
          dataSource={students?.filter(
            (item) =>
              item.classStudentStatusCode === 'RE' ||
              item.classStudentStatusCode === 'MB' ||
              item.classStudentStatusCode === 'GR',
          )}
          pagination={{
            pageSize: 9999999,
          }}
          onRow={(record) => {
            return {
              onClick: () => {
                history.push(
                  `/student/student?memberCode=${record.memberCode}&campusCode=${query.campus}`,
                );
              },
            };
          }}
          rowClassName="pointer"
          rowKey="classStudentCode"
        />
      </div>
      <div className="comm-bx__between">
        <h3 className="sub-title">{chosenClass?.className}</h3>
      </div>
      <div className="comm-table__area">
        <Table
          columns={columns}
          dataSource={students?.filter(
            (item) =>
              item.classStudentStatusCode !== 'RE' &&
              item.classStudentStatusCode !== 'MB' &&
              item.classStudentStatusCode !== 'GR',
          )}
          pagination={{
            pageSize: 9999999,
          }}
        />
      </div>

      {showScoreVisible && (
        <ShowScoreModal
          visible={showScoreVisible}
          setVisible={setShowScoreVisible}
          classCode={chosenClass?.classCode}
          memberCode={memberCode}
        />
      )}
    </>
  );
};

export default ClassStudentList;
